import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  contentChildren,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';

/**
 * Panneau d'onglet : contenu projeté, affiché quand l'onglet est actif.
 * Le contenu reste monté quand l'onglet est inactif (masquage CSS), afin de
 * conserver l'état des formulaires et les données déjà chargées.
 *
 * Usage :
 *  <idra-tabs>
 *    <idra-tab label="Connexion" icon="pi pi-microsoft">…</idra-tab>
 *    <idra-tab label="Boîtes">…</idra-tab>
 *  </idra-tabs>
 */
@Component({
  selector: 'idra-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'tabpanel',
    '[hidden]': '!active()',
  },
  styles: `
    :host {
      display: block;
    }

    :host([hidden]) {
      display: none;
    }
  `,
  template: `<ng-content />`,
})
export class IdraTab {
  readonly label = input.required<string>();
  readonly icon = input<string | undefined>(undefined);

  /** Piloté par le conteneur idra-tabs. */
  readonly active = signal(false);
}

/** Orientation de la barre d'onglets. */
export type IdraTabsOrientation = 'horizontal' | 'vertical';

/**
 * Conteneur d'onglets du kit interne : barre de titres (tablist accessible,
 * navigation aux flèches) + panneaux projetés. L'index actif est exposé en
 * [(index)] pour pilotage externe si besoin.
 *
 * En orientation `vertical`, la barre passe sur le côté (utile pour des
 * sous-onglets d'une section dense). Les onglets imbriqués sont supportés :
 * la navigation clavier est bornée aux onglets directs du conteneur.
 */
@Component({
  selector: 'idra-tabs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.vertical]': `orientation() === 'vertical'`,
  },
  styles: `
    :host {
      display: block;
    }

    /* Orientation verticale : barre latérale + panneaux côte à côte. */
    :host(.vertical) {
      display: flex;
      gap: 24px;
    }

    .tab-bar {
      display: flex;
      gap: 4px;
      margin-bottom: 16px;
      border-bottom: 1px solid var(--idra-border);
      overflow-x: auto;
    }

    :host(.vertical) .tab-bar {
      flex-direction: column;
      gap: 2px;
      flex-shrink: 0;
      margin-bottom: 0;
      border-bottom: 0;
      border-right: 1px solid var(--idra-border);
      overflow-x: visible;
      min-width: 200px;
    }

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      margin-bottom: -1px;
      border: 0;
      border-bottom: 2px solid transparent;
      background: transparent;
      color: var(--idra-text-secondary);
      font: inherit;
      font-size: 14px;
      font-weight: 700;
      white-space: nowrap;
      cursor: pointer;
    }

    :host(.vertical) .tab {
      margin-bottom: 0;
      margin-right: -1px;
      padding: 10px 16px;
      border-bottom: 0;
      border-right: 2px solid transparent;
      border-radius: var(--idra-radius-xs) 0 0 var(--idra-radius-xs);
      text-align: left;
      justify-content: flex-start;
    }

    .tab:hover {
      color: var(--idra-text);
    }

    .tab.active {
      color: var(--idra-primary);
      border-bottom-color: var(--idra-primary);
    }

    :host(.vertical) .tab.active {
      border-bottom-color: transparent;
      border-right-color: var(--idra-primary);
      background: var(--idra-surface-hover);
    }

    .tab:focus-visible {
      outline: none;
      box-shadow: var(--idra-focus-ring);
      border-radius: var(--idra-radius-xs);
    }

    .tab i {
      font-size: 14px;
    }

    /* Panneaux : occupent l'espace restant en orientation verticale. */
    .tab-panels {
      min-width: 0;
    }

    :host(.vertical) .tab-panels {
      flex: 1;
    }
  `,
  template: `
    <div class="tab-bar" role="tablist" [attr.aria-orientation]="orientation()">
      @for (tab of tabs(); track $index; let i = $index) {
        <button
          type="button"
          role="tab"
          class="tab"
          [class.active]="index() === i"
          [attr.aria-selected]="index() === i"
          [tabindex]="index() === i ? 0 : -1"
          (click)="select(i)"
          (keydown)="onKeydown($event, i)"
        >
          @if (tab.icon(); as icon) {
            <i [class]="icon" aria-hidden="true"></i>
          }
          {{ tab.label() }}
        </button>
      }
    </div>
    <div class="tab-panels">
      <ng-content />
    </div>
  `,
})
export class IdraTabs {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly tabs = contentChildren(IdraTab);

  /** Index de l'onglet actif — utilisable en [(index)]. */
  readonly index = model(0);

  /** Orientation de la barre : `horizontal` (défaut) ou `vertical`. */
  readonly orientation = input<IdraTabsOrientation>('horizontal');

  constructor() {
    // Répercute l'index actif sur les panneaux (et le borne si un onglet disparaît).
    effect(() => {
      const tabs = this.tabs();
      const active = Math.max(0, Math.min(this.index(), tabs.length - 1));
      tabs.forEach((tab, i) => tab.active.set(i === active));
    });
  }

  protected select(index: number): void {
    this.index.set(index);
  }

  /**
   * Navigation clavier du tablist : flèches (gauche/droite en horizontal,
   * haut/bas en vertical) + Home/End. Les deux jeux de flèches sont acceptés
   * quelle que soit l'orientation pour rester tolérant.
   */
  protected onKeydown(event: KeyboardEvent, current: number): void {
    const count = this.tabs().length;
    let next: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
    }

    if (next !== null) {
      event.preventDefault();
      this.select(next);
      // Bornage aux onglets directs : évite de capter ceux de tabs imbriquées.
      const buttons = this.host.nativeElement.querySelectorAll<HTMLButtonElement>(
        ':scope > .tab-bar > [role="tab"]',
      );
      buttons[next]?.focus();
    }
  }
}
